import { useState, useEffect, useMemo } from "react";
import { AlertCircle, CheckCircle, Info, AlertTriangle } from "lucide-react";

/**
 * SEO Helper Component
 * Provides auto-generation of SEO fields and real-time validation warnings
 */

// SEO field generators
export const generateMetaTitle = (name, village, location) => {
  if (!name) return "";
  
  let parts = [name];
  if (village) parts.push(village);
  if (location) parts.push(location);
  
  let title = parts.join(" | ");
  
  // Clean up: remove duplicate separators, trim spaces
  title = title.replace(/\|\s*\|/g, "|").trim();
  
  // Convert to Title Case
  title = title.split(" ").map(word => {
    if (word === "|") return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(" ");
  
  // Target 50-60 characters - truncate if needed
  if (title.length > 60) {
    title = title.substring(0, 57) + "...";
  }
  
  return title;
};

/**
 * Generate SEO-optimized meta description for homestay listings
 * 
 * Primary Template (with price): 
 *   "Book <Name> in <Village> <Location>. Comfortable stay with modern amenities. Prices from ₹<Price>/night. Book now."
 * 
 * Fallback Template (no price):
 *   "Book <Name> in <Village> <Location>. Comfortable stay with modern amenities and great guest reviews. Book now."
 * 
 * Hard Fallback (if over 175 chars):
 *   "Stay at <Name> in <Location>. Comfortable rooms, friendly hosts, and great value. Book now."
 * 
 * Rules:
 * - Max 175 characters
 * - Include pricing only if available
 * - Format price as "from ₹<price>/night"
 * - Remove village if not selected
 * - No pipe symbols (|)
 * - Auto-truncate without cutting words
 */
export const generateMetaDescription = (name, village, location, price = null) => {
  if (!name) return "";
  
  const MAX_LENGTH = 175;
  
  // Build location part (village + location or just location)
  let locationPart = "";
  if (village && location) {
    locationPart = `${village}, ${location}`;
  } else if (location) {
    locationPart = location;
  } else if (village) {
    locationPart = village;
  }
  
  // Clean the name - remove any pipe symbols
  const cleanName = name.replace(/\|/g, '-').trim();
  
  let description = "";
  
  // Primary template with price
  if (price && price > 0) {
    const formattedPrice = new Intl.NumberFormat('en-IN').format(price);
    description = `Book ${cleanName}`;
    if (locationPart) {
      description += ` in ${locationPart}`;
    }
    description += `. Comfortable stay with modern amenities. Prices from ₹${formattedPrice}/night. Book now.`;
  } else {
    // Fallback template without price
    description = `Book ${cleanName}`;
    if (locationPart) {
      description += ` in ${locationPart}`;
    }
    description += `. Comfortable stay with modern amenities and great guest reviews. Book now.`;
  }
  
  // Check if within limit
  if (description.length <= MAX_LENGTH) {
    return cleanDescription(description);
  }
  
  // Try without village (if we have both village and location)
  if (village && location) {
    if (price && price > 0) {
      const formattedPrice = new Intl.NumberFormat('en-IN').format(price);
      description = `Book ${cleanName} in ${location}. Comfortable stay with modern amenities. Prices from ₹${formattedPrice}/night. Book now.`;
    } else {
      description = `Book ${cleanName} in ${location}. Comfortable stay with modern amenities and great guest reviews. Book now.`;
    }
    
    if (description.length <= MAX_LENGTH) {
      return cleanDescription(description);
    }
  }
  
  // Hard fallback - shortest version
  description = `Stay at ${cleanName}`;
  if (location) {
    description += ` in ${location}`;
  }
  description += `. Comfortable rooms, friendly hosts, and great value. Book now.`;
  
  if (description.length <= MAX_LENGTH) {
    return cleanDescription(description);
  }
  
  // Final truncation - cut at word boundary
  return truncateAtWord(description, MAX_LENGTH);
};

// Helper: Clean description - remove extra spaces, pipe symbols, trim
const cleanDescription = (text) => {
  return text
    .replace(/\|/g, '') // Remove pipe symbols
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/\s+\./g, '.') // Remove space before period
    .replace(/\.+/g, '.') // Remove multiple periods
    .trim();
};

// Helper: Truncate at word boundary without cutting words
const truncateAtWord = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  
  // Find the last space before maxLength
  let truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSpace > maxLength - 30) {
    truncated = truncated.substring(0, lastSpace);
  }
  
  // Remove trailing punctuation and add period if needed
  truncated = truncated.replace(/[,\s]+$/, '');
  if (!truncated.endsWith('.')) {
    truncated += '.';
  }
  
  return truncated;
};

export const generateSlug = (name, village, location) => {
  if (!name) return "";
  
  let parts = [name];
  if (village) parts.push(village);
  if (location) parts.push(location);
  
  let slug = parts.join("-");
  
  // Convert to lowercase, replace spaces with hyphens
  slug = slug.toLowerCase();
  // Remove special characters except hyphens
  slug = slug.replace(/[^a-z0-9\s-]/g, "");
  // Replace multiple spaces/hyphens with single hyphen
  slug = slug.replace(/[\s_-]+/g, "-");
  // Remove leading/trailing hyphens
  slug = slug.replace(/^-+|-+$/g, "");
  
  return slug;
};

// Validation rules
const validateMetaTitle = (title) => {
  const warnings = [];
  
  if (!title) {
    warnings.push({ type: "error", message: "Meta title is missing" });
  } else if (title.length < 30) {
    warnings.push({ type: "warning", message: `Meta title too short (${title.length}/30-60 chars)` });
  } else if (title.length > 60) {
    warnings.push({ type: "warning", message: `Meta title too long (${title.length}/60 chars max)` });
  } else {
    warnings.push({ type: "success", message: `Meta title length is optimal (${title.length} chars)` });
  }
  
  return warnings;
};

const validateMetaDescription = (description) => {
  const warnings = [];
  const MAX_LENGTH = 175; // Updated limit per SEO requirements
  
  if (!description) {
    warnings.push({ type: "error", message: "Meta description is missing" });
  } else if (description.length < 100) {
    warnings.push({ type: "warning", message: `Meta description too short (${description.length}/120-175 chars recommended)` });
  } else if (description.length > MAX_LENGTH) {
    warnings.push({ type: "error", message: `Meta description too long (${description.length}/${MAX_LENGTH} chars max)` });
  } else if (description.length >= 100 && description.length <= MAX_LENGTH) {
    warnings.push({ type: "success", message: `Meta description length is optimal (${description.length}/${MAX_LENGTH} chars)` });
  }
  
  return warnings;
};

const validateSlug = (slug) => {
  const warnings = [];
  
  if (!slug) {
    warnings.push({ type: "error", message: "URL slug is missing" });
  } else if (!/^[a-z0-9-]+$/.test(slug)) {
    warnings.push({ type: "error", message: "Slug contains invalid characters (use only lowercase letters, numbers, hyphens)" });
  } else {
    warnings.push({ type: "success", message: "URL slug is valid" });
  }
  
  return warnings;
};

const validateFeaturedImage = (image) => {
  const warnings = [];
  
  if (!image) {
    warnings.push({ type: "warning", message: "No featured image selected (affects OG tags & Schema)" });
  } else {
    warnings.push({ type: "success", message: "Featured image is set" });
  }
  
  return warnings;
};

const validateLocation = (locationId) => {
  const warnings = [];
  
  if (!locationId) {
    warnings.push({ type: "error", message: "Location is missing (SEO critical)" });
  } else {
    warnings.push({ type: "success", message: "Location is set" });
  }
  
  return warnings;
};

// SEO Score calculation
export const calculateSEOScore = (form, featuredImage) => {
  let score = 0;
  let maxScore = 100;
  const META_DESC_MAX = 175; // Updated limit
  
  // Meta title (25 points)
  if (form.meta_title) {
    if (form.meta_title.length >= 30 && form.meta_title.length <= 60) {
      score += 25;
    } else if (form.meta_title.length > 0) {
      score += 15;
    }
  }
  
  // Meta description (25 points) - updated for 175 char limit
  if (form.meta_description) {
    if (form.meta_description.length >= 100 && form.meta_description.length <= META_DESC_MAX) {
      score += 25;
    } else if (form.meta_description.length > 0 && form.meta_description.length <= META_DESC_MAX) {
      score += 15;
    } else if (form.meta_description.length > META_DESC_MAX) {
      score += 10; // Penalty for being over limit
    }
  }
  
  // Slug (20 points)
  if (form.slug && /^[a-z0-9-]+$/.test(form.slug)) {
    score += 20;
  } else if (form.slug) {
    score += 10;
  }
  
  // Location (15 points)
  if (form.location_id) {
    score += 15;
  }
  
  // Featured image (15 points)
  if (featuredImage || form.og_image) {
    score += 15;
  }
  
  return Math.round((score / maxScore) * 100);
};

// Main SEO Validation Panel Component
export const SEOValidationPanel = ({ form, featuredImage, locations, subLocations }) => {
  const [expanded, setExpanded] = useState(true);
  
  // Get location and village names
  const locationName = useMemo(() => {
    const loc = locations?.find(l => l.id === form.location_id);
    return loc?.name || "";
  }, [locations, form.location_id]);
  
  const villageName = useMemo(() => {
    const sub = subLocations?.find(s => s.id === form.sub_location_id);
    return sub?.name || "";
  }, [subLocations, form.sub_location_id]);
  
  // Calculate SEO score
  const seoScore = useMemo(() => {
    return calculateSEOScore(form, featuredImage);
  }, [form, featuredImage]);
  
  // Collect all warnings
  const allWarnings = useMemo(() => {
    return [
      ...validateMetaTitle(form.meta_title),
      ...validateMetaDescription(form.meta_description),
      ...validateSlug(form.slug),
      ...validateFeaturedImage(featuredImage || form.og_image),
      ...validateLocation(form.location_id),
    ];
  }, [form, featuredImage]);
  
  const errors = allWarnings.filter(w => w.type === "error");
  const warnings = allWarnings.filter(w => w.type === "warning");
  const successes = allWarnings.filter(w => w.type === "success");
  
  // Score color
  const scoreColor = seoScore >= 80 ? "text-green-600" : seoScore >= 50 ? "text-amber-600" : "text-red-600";
  const scoreBg = seoScore >= 80 ? "bg-green-100" : seoScore >= 50 ? "bg-amber-100" : "bg-red-100";
  
  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid="seo-validation-panel">
      {/* Header */}
      <button
        type="button"
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full ${scoreBg} flex items-center justify-center`}>
            <span className={`text-lg font-bold ${scoreColor}`}>{seoScore}</span>
          </div>
          <div className="text-left">
            <h4 className="font-semibold text-foreground">SEO Score</h4>
            <p className="text-xs text-muted-foreground">
              {errors.length > 0 ? `${errors.length} errors` : ""}
              {errors.length > 0 && warnings.length > 0 ? ", " : ""}
              {warnings.length > 0 ? `${warnings.length} warnings` : ""}
              {errors.length === 0 && warnings.length === 0 ? "All checks passed!" : ""}
            </p>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {/* Content */}
      {expanded && (
        <div className="p-4 space-y-3 border-t">
          {/* Errors */}
          {errors.map((item, idx) => (
            <div key={`error-${idx}`} className="flex items-start gap-2 p-2 bg-red-50 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-red-700">{item.message}</span>
            </div>
          ))}
          
          {/* Warnings */}
          {warnings.map((item, idx) => (
            <div key={`warning-${idx}`} className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-amber-700">{item.message}</span>
            </div>
          ))}
          
          {/* Success */}
          {errors.length === 0 && warnings.length === 0 && (
            <div className="flex items-start gap-2 p-2 bg-green-50 rounded-lg">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-green-700">All SEO checks passed! Your listing is well optimized.</span>
            </div>
          )}
          
          {/* Tips */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-700">
                <p className="font-medium mb-1">SEO Tips:</p>
                <ul className="space-y-0.5 list-disc ml-4">
                  <li>Meta title: 50-60 characters for optimal display</li>
                  <li>Meta description: 120-175 characters (max 175)</li>
                  <li>Always add a featured image for rich snippets</li>
                  <li>Location helps with local SEO rankings</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Schema.org JSON-LD Generator
export const generateSchemaOrg = (homestay, baseUrl) => {
  if (!homestay) return null;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": homestay.property_type === "Hotel" ? "Hotel" : "LodgingBusiness",
    "name": homestay.name,
    "url": `${baseUrl}/homestay/${homestay.slug || homestay.id}`,
    "image": homestay.featured_image || homestay.og_image || (homestay.images?.[0] ? `${baseUrl}${homestay.images[0]}` : null),
    "description": homestay.meta_description || homestay.description,
  };
  
  // Address
  if (homestay.address) {
    schema.address = {
      "@type": "PostalAddress",
      "streetAddress": homestay.address,
    };
    
    if (homestay.sub_location_name) {
      schema.address.addressLocality = homestay.sub_location_name;
    }
    if (homestay.location_name) {
      schema.address.addressRegion = homestay.location_name;
    }
    schema.address.addressCountry = "IN";
  }
  
  // Telephone
  if (homestay.mobile_number) {
    schema.telephone = homestay.mobile_number;
  }
  
  // Price range
  if (homestay.price_per_night) {
    schema.priceRange = `${homestay.price_currency || 'INR'} ${homestay.price_per_night}`;
  }
  
  // Aggregate rating
  if (homestay.average_rating > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": homestay.average_rating.toFixed(1),
      "reviewCount": homestay.review_count || 0,
      "bestRating": "5",
      "worstRating": "1"
    };
  }
  
  // Geo coordinates
  if (homestay.latitude && homestay.longitude) {
    schema.geo = {
      "@type": "GeoCoordinates",
      "latitude": homestay.latitude,
      "longitude": homestay.longitude
    };
  }
  
  // Check-in/out times
  if (homestay.check_in_time) {
    schema.checkinTime = homestay.check_in_time;
  }
  if (homestay.check_out_time) {
    schema.checkoutTime = homestay.check_out_time;
  }
  
  // Amenities
  if (homestay.facilities?.length > 0) {
    schema.amenityFeature = homestay.facilities.map(facility => ({
      "@type": "LocationFeatureSpecification",
      "name": facility,
      "value": true
    }));
  }
  
  return schema;
};

export default SEOValidationPanel;
